import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import i18n from "../i18n";

// Create the context
const SiteContext = createContext();

// Custom hook for using the context
export const useSiteContext = () => {
  return useContext(SiteContext);
};

export const SiteProvider = ({ children }) => {
  const [sections, setSections] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [translateData, setTranslateData] = useState([]);
  const [currentLanguageCode, setCurrentLanguageCode] = useState("tr");
  const [currentLanguageName, setCurrentLanguageName] = useState("Türkçe");
  const [loading, setLoading] = useState(true);

  const normalizeApiUrl = (value) => {
    const fallback = "https://webapi.mngairlines.com/api";
    if (!value || typeof value !== "string") return fallback;

    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed.replace(/\/+$/, "");
    }

    return `https://${trimmed.replace(/\/+$/, "")}`;
  };

  const apiUrl = normalizeApiUrl(
    process.env.REACT_APP_BASE_API_URL || process.env.REACT_APP_API_URL,
  );
  const testUrl = (process.env.REACT_APP_TEST_URL || "").trim();

  if (!apiUrl) {
    console.error("API URL is not set");
  }

  useEffect(() => {
    const getDomainCandidates = () => {
      const candidates = [];
      const currentHost = window.location.hostname;
      const envHost = testUrl;

      if (currentHost) candidates.push(currentHost);
      if (envHost) candidates.push(envHost);
      if (!candidates.includes("mngairlines.com")) {
        candidates.push("mngairlines.com");
      }

      return [...new Set(candidates)];
    };

    const resolveWebsiteGuid = async () => {
      const domains = getDomainCandidates();

      for (const domain of domains) {
        try {
          const response = await axios.get(
            `${apiUrl}/website/getdomain/${domain}?timestamp=${new Date().getTime()}`,
          );
          const guid = response.data?.data?.guid;
          if (guid) {
            return { guid, domain };
          }
        } catch (error) {
          // Try next domain candidate
        }
      }

      return null;
    };

    const initializeSite = async () => {
      try {
        const resolvedWebsite = await resolveWebsiteGuid();

        if (resolvedWebsite?.guid) {
          const websiteGuid = resolvedWebsite.guid;

          // 2. and 3. Get Section Content and Translations (Concurrently)
          const [sectionsResponse, translateResponse] = await Promise.all([
             axios.get(`${apiUrl}/sectioncontent/list/${websiteGuid}?timestamp=${new Date().getTime()}`),
             axios.get(`${apiUrl}/translate/list?timestamp=${new Date().getTime()}`)
          ]);

          let fetchedSections = sectionsResponse.data?.data || [];

          // Filter by websiteGuid if needed (as per Angular code)
          // Angular code: result = result.data.filter((x: any) => x.sectionContentWebsiteDtos[0]?.websiteGuid == websiteGuid);
          // Let's log if this filter is being too aggressive
          const preFilterCount = fetchedSections.length;
          fetchedSections = fetchedSections.filter((x) => {
            const match =
              x.sectionContentWebsiteDtos?.[0]?.websiteGuid === websiteGuid;
            if (!match && filteredLogCount < 5) {
              // console.log('Filtering out section due to GUID mismatch:', x.sectionContentWebsiteDtos?.[0]?.websiteGuid, 'vs', websiteGuid);
              filteredLogCount++;
            }
            return match;
          });

          setTranslateData(translateResponse.data?.data?.reverse() || []);
          setSections(fetchedSections.reverse());
          processSectionData(fetchedSections);

          // Handle local storage for language
          const localStorageData = localStorage.getItem("localization");
          if (localStorageData) {
            try {
              const parsed = JSON.parse(localStorageData);
              if (parsed.code) {
                setCurrentLanguageCode(parsed.code);
                i18n.changeLanguage(parsed.code);
              }
              if (parsed.name) setCurrentLanguageName(parsed.name);
              if (parsed.rtl)
                document.documentElement.setAttribute("dir", "rtl");
            } catch (e) {}
          }
        } else {
          console.error(
            "SiteContext - website guid not found for domains:",
            getDomainCandidates(),
          );
        }
      } catch (error) {
        console.error("Error initializing site context:", error);
      } finally {
        setLoading(false);
      }
    };

    let filteredLogCount = 0;
    initializeSite();
  }, [apiUrl, testUrl]);

  // Recursive function to fill empty values with English fallback
  const fillEmptyValuesWithEnglish = (target, source) => {
    if (!target || !source) return;
    Object.keys(target).forEach((key) => {
      const obj = target[key];
      const srcObj = source[key];
      if (
        typeof obj === "object" &&
        obj !== null &&
        typeof srcObj === "object" &&
        srcObj !== null
      ) {
        fillEmptyValuesWithEnglish(obj, srcObj);
      } else {
        if (obj === "" || obj === null) {
          target[key] = srcObj;
        }
      }
    });
  };

  // Process sections to handle fallbacks
  const processSectionData = (currentSections) => {
    // Deep copy to avoid mutating state directly if called multiple times on same obj
    // But here we are processing fresh data usually

    // Logic to fill missing translations with English
    const sectionsWithFallback = currentSections.map((section) => {
      // We only need to process non-English sections
      const contentJson = JSON.parse(section.content || "{}");
      if (contentJson?.lang !== "en") {
        const sectionEn = currentSections.find(
          (x) =>
            x.sectionHandle === section.sectionHandle &&
            JSON.parse(x.content || "{}")?.lang === "en",
        );

        if (sectionEn && sectionEn.content) {
          const sourceJson = JSON.parse(sectionEn.content);
          fillEmptyValuesWithEnglish(contentJson.data, sourceJson.data);
          // Return a new object with updated content
          return { ...section, content: JSON.stringify(contentJson) };
        }
      }
      return section;
    });

    setSectionData(sectionsWithFallback);
  };

  const changeLanguage = (languageCode, languageName, rtl) => {
    localStorage.setItem(
      "localization",
      JSON.stringify({
        code: languageCode,
        name: languageName,
        rtl: rtl,
      }),
    );
    setCurrentLanguageCode(languageCode);
    setCurrentLanguageName(languageName);
    i18n.changeLanguage(languageCode);
    document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");

    // Re-process sections if needed, though they are already loaded
    // In the Angular code, this triggers setSectionData again, theoretically refreshing if sections changed
    // implementation:
    processSectionData(sections);
  };

  const getSection = (name) => {
    if (!sectionData || sectionData.length === 0) {
      return null;
    }

    const normalizedLang = currentLanguageCode.toLowerCase();

    // Debug logging
    // console.log(`Attempting to find section '${name}' for lang '${normalizedLang}'`);

    const section = sectionData.find((x) => {
      const content = x.content ? JSON.parse(x.content) : {};
      return x.sectionHandle === name && content?.lang === normalizedLang;
    });

    if (!section) {
      console.warn(
        `Section '${name}' not found for language '${normalizedLang}'. Available handles:`,
        sectionData
          .filter((x) => JSON.parse(x.content || "{}")?.lang === normalizedLang)
          .map((x) => x.sectionHandle),
      );

      // Try fallback to English explicitly if needed (though processSectionData handles fallback content, it doesn't duplicate the section object for other languages if they don't exist as separate entry?
      // Wait, Angular logic: find by Lang. If not found, returns null.
      // But processSectionData FILLS english data INTO the non-english section. It relies on the non-english section EXISTING.
      // If the section ONLY exists in English, and we request 'tr', it returns NULL.
      // Check if Angular does fallback for the section OBJECT itself?
      // Angular: getSection searches for lang. If not found, returns null.
      // So if 'test_yagiz' is only defined for 'en', and current lang is 'tr', it returns null.

      // Should we fallback to English section if current lang section missing?
      // Angular:
      /*
            if (!section)
              section = this.sectionData?.value.find(
                (x: any) =>
                  x.sectionHandle == sectionName &&
                  JSON.parse(x.content ?? "[]")?.lang == "en"
              );
            */
      // Wait, I missed this part in the shared/language.service.ts file content review in Step 9?
      // Let's re-read Step 9 language.service.ts.
      // Lines 111-119 (getSection)
      /*
              getSection(name: string) {
                const section = this.sectionData?.value?.find(
                  (x: any) =>
                    x.sectionHandle == name &&
                    JSON.parse(x.content ?? "[]")?.lang ==
                    this.currentLanguageCode.value.toLowerCase()
                );
                return section ? JSON.parse(section.content)?.data : null;
              }
            */
      // It DOES NOT look for English fallback inside getSection.
      // However, getInput (line 129) DOES fallback.

      return null;
    }

    return JSON.parse(section.content)?.data;
  };

  const getTranslation = (guid) => {
    const translation = translateData.find(
      (x) =>
        x.language === currentLanguageCode.toLowerCase() && x.guidKey === guid,
    );

    if (translation) return translation.value;

    const original = translateData.find(
      (x) => x.language === "en" && x.guidKey === guid,
    );

    if (original) return original.value;
    return "";
  };

  return (
    <SiteContext.Provider
      value={{
        sections,
        sectionData,
        translateData,
        currentLanguageCode,
        currentLanguageName,
        loading,
        changeLanguage,
        getSection,
        getTranslation,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};
